import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from '@wf/components';
import { Label } from '@wf/sw-ui-kit';

import Separator from '../../../../../components/Separator';
import { LicenseField } from '../../LicenseField';

import { ArrayFields, BeneficiaryFields } from '../../../types/fieldEnums';
import { beneficiaryConfigNames } from '../../../types/configNames';
import { PHONE_REGEXP } from '../../../../../constants/regex';

import { type ICompanyRimmEntity } from '../../../../rimm/data/rimmEntityTypes';
import { RimmEntityType } from '../../../../rimm/data/rimmEntityEnums';

import styles from './BeneficiaryPopup.module.scss';

interface IBeneficiaryPopup {
  index: Nullable<number>;
  onClose: () => void;
  onChange: () => void;
  type: Nullable<'add' | 'update'>;
}

function BeneficiaryPopup({ index, onClose, onChange, type }: IBeneficiaryPopup) {
  const { t } = useTranslation();
  const { setValue } = useFormContext();

  const onBeneficiaryChange = (value: (ICompanyRimmEntity & SelectOption) | null) => {
    const address1: string = value?.address1 ?? '';
    const address2: string = value?.address2 ?? '';
    setValue(
      `${ArrayFields.beneficiaries}.${index as number}.${BeneficiaryFields.description}`,
      `${address1} ${address2}`
    );
    setValue(`${ArrayFields.beneficiaries}.${index as number}.${BeneficiaryFields.phoneNumber}`, value?.phoneNumber);
    setValue(`${ArrayFields.beneficiaries}.${index as number}.${BeneficiaryFields.email}`, value?.email);
  };

  return (
    <Modal isOpen={index !== null} handleClose={onClose} id="attachment-modal" className={styles.modal}>
      <Modal.Header>
        <Modal.Title>{type === 'add' ? t('Add New Beneficiary') : t('Edit Beneficiary')}</Modal.Title>
      </Modal.Header>
      <Modal.Content className={styles.content}>
        <div className={styles.form}>
          <LicenseField.RimmSelectField
            onChange={(value) => onBeneficiaryChange(value as (ICompanyRimmEntity & SelectOption) | null)}
            name={`${ArrayFields.beneficiaries}.${index as number}.${BeneficiaryFields.code}`}
            configName={beneficiaryConfigNames[BeneficiaryFields.code]}
            label={<Label>{t('Beneficiary Code')}</Label>}
            placeholder={t('Search Beneficiary Code')}
            entity={RimmEntityType.company}
            minSuggest={2}
            withTag
          />
          <LicenseField.TextField
            name={`${ArrayFields.beneficiaries}.${index as number}.${BeneficiaryFields.description}`}
            configName={beneficiaryConfigNames[BeneficiaryFields.code]}
            label={<Label>{t('Beneficiary name & Address')}</Label>}
            placeholder={t('Beneficiary name')}
            textarea
            disabled
          />
          <Separator />
          <div className={styles.contacts}>
            <LicenseField.TextField
              name={`${ArrayFields.beneficiaries}.${index as number}.${BeneficiaryFields.phoneNumber}`}
              configName={beneficiaryConfigNames[BeneficiaryFields.phoneNumber]}
              label={<Label>{t('Beneficiary Phone')}</Label>}
              placeholder={t('Write phone number')}
              regexp={PHONE_REGEXP}
            />
            <LicenseField.TextField
              name={`${ArrayFields.beneficiaries}.${index as number}.${BeneficiaryFields.email}`}
              configName={beneficiaryConfigNames[BeneficiaryFields.email]}
              label={<Label>{t('Beneficiary Email')}</Label>}
              placeholder={t('Write email')}
            />
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Button onClick={onClose} color="typography" secondary>
          {t('Cancel')}
        </Button>
        <Button onClick={onChange}>{t(type === 'add' ? 'Add' : 'Update')}</Button>
      </Modal.Footer>
    </Modal>
  );
}

export default BeneficiaryPopup;
